/**
 * The build's environment, read once.
 *
 * A copy of the Studio's, deliberately: the two apps are separate deploy targets with
 * separate `.env` files, and a shared module here would only make one app's misconfiguration
 * the other's problem. What they must agree on — the project, the region, the database name,
 * the bucket — comes from `@atlas-nook/brand`, which is where agreement belongs.
 *
 * `VITE_USE_EMULATOR` is a **dev-only** switch: a production build ignores it, so a stray
 * `.env` can never point a deployed admin app at localhost.
 */

import { STORAGE_BUCKET } from '@atlas-nook/brand';

/** The emulator ports, from `apps/studio/firebase/firebase.json` — one emulator suite, two apps. */
export const EMULATOR = {
  authUrl: 'http://127.0.0.1:9399',
  firestoreHost: '127.0.0.1',
  firestorePort: 8181,
  storageHost: '127.0.0.1',
  storagePort: 9199,
  functionsHost: '127.0.0.1',
  functionsPort: 5101,
} as const;

export interface AdminEnv {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  useEmulator: boolean;
}

function read(key: string): string {
  const value = import.meta.env[key];
  return typeof value === 'string' ? value : '';
}

export function adminEnv(): AdminEnv {
  const useEmulator = import.meta.env.DEV && read('VITE_USE_EMULATOR') === 'true';

  return {
    apiKey: read('VITE_FIREBASE_API_KEY') || (useEmulator ? 'emulator' : ''),
    authDomain: read('VITE_FIREBASE_AUTH_DOMAIN') || (useEmulator ? 'localhost' : ''),
    projectId: read('VITE_FIREBASE_PROJECT_ID') || (useEmulator ? 'atlas-nook' : ''),
    storageBucket: read('VITE_FIREBASE_STORAGE_BUCKET') || (useEmulator ? STORAGE_BUCKET : ''),
    messagingSenderId: read('VITE_FIREBASE_MESSAGING_SENDER_ID'),
    appId: read('VITE_FIREBASE_APP_ID') || (useEmulator ? 'admin-emulator' : ''),
    useEmulator,
  };
}
